import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AgeGroup } from '@court-sync/shared';
import { ageGroupService } from '../services/ageGroup';
import { useAuthStore } from '../store/auth';
import { isPublicBrowsePath } from '../lib/isPublicBrowsePath';
import { STALE_TIME, queryClient } from '../queryClient';
import { queryKeys } from './keys';
import { buildAgeGroupCalendarColorLookup } from '../utils/lessonSlotColors';

export function useAgeGroups(academyId?: string, options?: { enabled?: boolean }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated && s.isInitialized);
  const onPublicBrowse =
    typeof window !== 'undefined' && isPublicBrowsePath(window.location.pathname);
  const usePublicApi = !isAuthenticated || onPublicBrowse;
  const enabled = (options?.enabled ?? true) && !!academyId;
  const queryKey = queryKeys.ageGroups.list(academyId ?? '', usePublicApi ? 'public' : 'private');

  const { data: ageGroups = [], isLoading, error, refetch } = useQuery<AgeGroup[]>({
    queryKey,
    queryFn: () =>
      usePublicApi
        ? ageGroupService.getPublicAgeGroups(academyId!)
        : ageGroupService.getAgeGroups(academyId!),
    enabled,
    staleTime: STALE_TIME.MEDIUM,
  });

  const getAgeGroupsByAcademy = useCallback(
    (id: string) => ageGroups.filter((g) => g.academyId === id),
    [ageGroups]
  );

  const scopedAgeGroups = useMemo(
    () => (academyId ? getAgeGroupsByAcademy(academyId) : ageGroups),
    [academyId, ageGroups, getAgeGroupsByAcademy]
  );

  const refresh = useCallback(() => refetch(), [refetch]);

  return {
    ageGroups: scopedAgeGroups,
    isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refresh,
    getAgeGroupsByAcademy,
  };
}

export async function fetchAgeGroupsQuery(
  academyId: string,
  options?: { force?: boolean }
): Promise<void> {
  const key = queryKeys.ageGroups.list(academyId);
  if (options?.force) {
    await queryClient.invalidateQueries({ queryKey: key });
  }
  await queryClient.fetchQuery({
    queryKey: key,
    queryFn: () => ageGroupService.getAgeGroups(academyId),
    staleTime: STALE_TIME.MEDIUM,
  });
}

export function invalidateAgeGroupsQueries(academyId: string): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.ageGroups.list(academyId) });
}

/** Cached age groups + calendar/form lookup maps (avoids per-render service fetches). */
export function useAgeGroupLookups(academyId?: string) {
  const { ageGroups, isLoading, error, refresh } = useAgeGroups(academyId);

  const activeAgeGroups = useMemo(
    () => ageGroups.filter((g) => g.isActive).sort((a, b) => a.sortOrder - b.sortOrder),
    [ageGroups]
  );

  const ageGroupById = useMemo(() => buildAgeGroupCalendarColorLookup(ageGroups), [ageGroups]);

  const ageGroupLabelById = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const group of ageGroups) {
      if (group.isActive === false) continue;
      labels[group.id] = group.label;
    }
    return labels;
  }, [ageGroups]);

  return {
    ageGroups,
    activeAgeGroups,
    ageGroupById,
    ageGroupLabelById,
    isLoading,
    error,
    refresh,
  };
}
